import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'

/**
 * IST-only timestamp handling for Pulse.
 *
 * Boldfit operates entirely in Asia/Kolkata (+05:30). Every timestamp that the
 * operator sees — order received, ACK'd, audit events — must render in IST
 * regardless of the browser's system timezone. A vendor sitting on a laptop
 * still on America/Los_Angeles cannot see a receiving time that says "yesterday"
 * when the warehouse in Bengaluru saw it drop three minutes ago.
 *
 * All display formatting goes through `formatIST`. Parsing goes through
 * `parseIST` — Vision returns ISO-8601 with an explicit `+05:30` suffix (once
 * the Java sibling worker's fix lands), which `new Date(...)` parses correctly
 * to the right instant. For legacy strings without a zone suffix, we interpret
 * them as IST wall-clock time so we don't silently shift by 5.5 hours.
 */

import { env } from '@/lib/env'

export const IST_ZONE = 'Asia/Kolkata'
export const IST_LOCALE = 'en-IN'

const ZONE = env.VITE_TIME_ZONE || IST_ZONE

const ZONE_SUFFIX_RE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/

export const DEFAULT_IST_PATTERN = 'yyyy-MM-dd HH:mm:ss XXX'

export function parseIST(input: string): Date {
  if (ZONE_SUFFIX_RE.test(input)) return new Date(input)
  return fromZonedTime(input, ZONE)
}

export function formatIST(
  input: Date | string,
  pattern: string = DEFAULT_IST_PATTERN,
): string {
  const date = typeof input === 'string' ? parseIST(input) : input
  return formatInTimeZone(date, ZONE, pattern)
}

export function nowIST(): Date {
  return new Date()
}
